import { FC } from "react";
import { Bar } from "react-chartjs-2";
import { HistogramBuckets } from "types/quiz";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Theme, useTheme } from "@mui/material/styles";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface HistogramProps {
  histogramBuckets: HistogramBuckets;
  userScore: number | null;
  userPercentile: number | null;
}

const roundToNearestPercentile = (num: number): number => {
  return Math.floor(num / 10) * 10;
};

const Histogram: FC<HistogramProps> = ({
  histogramBuckets,
  userScore,
  userPercentile,
}) => {
  const labels = Object.keys(histogramBuckets);
  const data = Object.values(histogramBuckets).map(Number);
  const theme = useTheme();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );

  const userBucket =
    userScore !== null ? roundToNearestPercentile(userScore).toString() : null;

  const backgroundColors = labels.map((label) =>
    label === userBucket
      ? theme.palette.secondary.main
      : theme.palette.primary.main
  );

  const borderColors = labels.map((label) =>
    label === userBucket
      ? theme.palette.secondary.main
      : theme.palette.primary.main
  );

  const chartData = {
    labels: labels,
    datasets: [
      {
        label: "Plays",
        data: data,
        backgroundColor: backgroundColors,
        borderColor: borderColors,
        borderWidth: 1,
      },
    ],
  };

  const options = {
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Plays",
          color: theme.palette.primary.main,
          font: {
            size: isMobile ? 10 : 16,
          },
        },
        ticks: {
          font: {
            size: isMobile ? 10 : 14,
          },
        },
      },
      x: {
        title: {
          display: true,
          text: "Percentage",
          color: theme.palette.primary.main,
          font: {
            size: isMobile ? 10 : 16,
          },
        },
        ticks: {
          font: {
            size: isMobile ? 10 : 14,
          },
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
    },
  };

  if (userPercentile !== null) {
    (options.plugins as any).title = {
      display: true,
      text: `You scored higher than ${userPercentile}% of people`,
      font: {
        size: isMobile ? 14 : 18,
      },
      color: theme.palette.primary.main,
    };
  }

  return (
    <div>
      <Bar data={chartData} options={options} />
    </div>
  );
};

export default Histogram;
